import { Check, Copy } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../button/button';
import { Tooltip } from '../tooltip/tooltip';

type CopyIconProps = {
  textToCopy: string;
  iconSize?: 'sm' | 'md' | 'lg';
  tooltipConfig?: { visible: false } | { visible: true; label: string };
};

/**
 * @param textToCopy - The text to copy to the clipboard.
 * @param iconSize - The size of the icon in pixels. 'sm' is 12x12px, 'md' is 16x16px, 'lg' is 20x20px. Defaults to 'md'.
 * @param tooltipConfig - Configuration for the tooltip. Either { visible: false } or { visible: true, label: string }.
 */
export const CopyIcon = ({ textToCopy, iconSize: size = 'md', tooltipConfig = { visible: false } }: CopyIconProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const handleOnClick = async () => {
    await navigator.clipboard.writeText(textToCopy);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 1000);
  };

  const button = (
    <Button variant="ghost" size="icon" onClick={handleOnClick} className="bg-transparent">
      {isCopied ? (
        <Check
          className={cn('text-success', {
            'h-3 w-3': size === 'sm',
            'h-4 w-4': size === 'md',
            'h-5 w-5': size === 'lg',
          })}
        />
      ) : (
        <Copy
          className={cn('text-muted-foreground', {
            'h-3 w-3': size === 'sm',
            'h-4 w-4': size === 'md',
            'h-5 w-5': size === 'lg',
          })}
        />
      )}
    </Button>
  );

  if (tooltipConfig.visible) {
    return <Tooltip trigger={button} content={tooltipConfig.label} contentAlign="center" />;
  }

  return button;
};
